import React, { useState, useEffect, useCallback } from 'react';
import { adminGetSellers, adminUpdateSellerStatus, adminGetOrders, adminGetStats, adminGetDashboard, adminResetSellerPassword,
         adminGetCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
         adminGetStockForecast, adminSetStockAlert, adminDeleteStockAlert, adminToggleSellerToucan } from '../marketApi';

function fmtDuration(minutes) {
  if (!minutes || minutes <= 0) return null;
  const m = Math.round(Number(minutes));
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function statusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return 'badge-success';
  if (s === 'rejected') return 'badge-error';
  return 'badge-warning';
}

function orderStatusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return 'badge-success';
  if (s === 'cancelled') return 'badge-error';
  if (s === 'confirmed') return 'badge-blue';
  if (s === 'packed') return 'badge-purple';
  return 'badge-warning';
}

function ResetPasswordModal({ seller, onClose }) {
  const [pwd, setPwd] = useState({ newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(''); setOk(false);
    if (pwd.newPassword.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (pwd.newPassword !== pwd.confirmPassword) { setErr('Passwords do not match'); return; }
    setSaving(true);
    try {
      await adminResetSellerPassword(seller.id, pwd.newPassword);
      setOk(true);
      setPwd({ newPassword: '', confirmPassword: '' });
    } catch (e) {
      setErr(e.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">🔒 Reset Password</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 14, fontSize: 14, color: 'var(--muted)' }}>
            Reset password for <strong style={{ color: 'var(--text)' }}>{seller.business_name || seller.phone}</strong>.
            The seller will be logged out from all devices.
          </div>
          {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
          {ok  && <div className="alert alert-success" style={{ marginBottom: 12 }}>Password reset successfully. Seller has been logged out.</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">New Password <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 12 }}>(min 6 characters)</span></label>
              <input className="form-control" type="password" placeholder="New password"
                value={pwd.newPassword}
                onChange={e => { setPwd(p => ({ ...p, newPassword: e.target.value })); setErr(''); setOk(false); }} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-control" type="password" placeholder="Repeat new password"
                value={pwd.confirmPassword}
                onChange={e => { setPwd(p => ({ ...p, confirmPassword: e.target.value })); setErr(''); setOk(false); }} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="button" className="btn btn-ghost btn-full" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
                {saving ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function SellersTab() {
  const [filter, setFilter] = useState('');
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});
  const [toucanUpdating, setToucanUpdating] = useState({});
  const [resetTarget, setResetTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminGetSellers(filter || undefined)
      .then((data) => {
        setSellers(Array.isArray(data) ? data : (data.content || []));
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Failed to load sellers');
        setLoading(false);
      });
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function handleAction(id, status) {
    setUpdating((prev) => ({ ...prev, [id]: true }));
    try {
      await adminUpdateSellerStatus(id, status);
      setSellers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status } : s))
      );
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setUpdating((prev) => ({ ...prev, [id]: false }));
    }
  }

  async function handleToucanToggle(id, currentValue) {
    const enabled = !currentValue;
    setToucanUpdating((prev) => ({ ...prev, [id]: true }));
    try {
      await adminToggleSellerToucan(id, enabled);
      setSellers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, toucan_enabled: enabled ? 1 : 0 } : s))
      );
    } catch (e) {
      setError(e.message || 'Failed to update payment setting');
    } finally {
      setToucanUpdating((prev) => ({ ...prev, [id]: false }));
    }
  }

  const FILTERS = ['', 'pending', 'approved', 'rejected'];
  const FILTER_LABELS = { '': 'All', pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}

      {/* Filter pills */}
      <div className="category-scroll" style={{ paddingTop: 0, paddingBottom: 12, marginBottom: 12 }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`chip${filter === f ? ' active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <div>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
          ))}
        </div>
      ) : sellers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏪</div>
          <h3>No sellers found</h3>
          <p style={{ marginTop: 6 }}>
            {filter ? `No ${filter} sellers` : 'No sellers have registered yet'}
          </p>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Owner</th>
                <th>Phone</th>
                <th>City</th>
                <th>Status</th>
                <th>Registered</th>
                <th>Online Pay</th>
                <th>Actions</th>
                <th>Password</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.business_name || s.businessName}</td>
                  <td>{s.owner_name || s.ownerName}</td>
                  <td style={{ fontFamily: 'monospace' }}>{s.phone}</td>
                  <td>{s.city}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass(s.status)}`}>
                      {s.status || 'pending'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: 12 }}>
                    {(s.created_at || s.createdAt)
                      ? new Date(s.created_at || s.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td>
                    <button
                      className={`btn btn-sm ${s.toucan_enabled ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => handleToucanToggle(s.id, s.toucan_enabled)}
                      disabled={toucanUpdating[s.id]}
                      title={s.toucan_enabled ? 'ToucanPay enabled — click to disable' : 'ToucanPay disabled — click to enable'}
                      style={s.toucan_enabled ? { background: '#6366f1', borderColor: '#6366f1', color: '#fff', minWidth: 72 } : { minWidth: 72 }}
                    >
                      {toucanUpdating[s.id] ? '…' : s.toucan_enabled ? '💳 ON' : '💳 OFF'}
                    </button>
                  </td>
                  <td>
                    {(s.status === 'pending' || !s.status) && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAction(s.id, 'approved')}
                          disabled={updating[s.id]}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleAction(s.id, 'rejected')}
                          disabled={updating[s.id]}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {s.status === 'approved' && (
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => handleAction(s.id, 'rejected')}
                        disabled={updating[s.id]}
                      >
                        Revoke
                      </button>
                    )}
                    {s.status === 'rejected' && (
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handleAction(s.id, 'approved')}
                        disabled={updating[s.id]}
                      >
                        Re-approve
                      </button>
                    )}
                    {updating[s.id] && (
                      <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 6 }}>
                        Saving...
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => setResetTarget(s)}
                      title="Reset seller password"
                    >
                      🔒 Reset
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resetTarget && (
        <ResetPasswordModal seller={resetTarget} onClose={() => setResetTarget(null)} />
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminGetOrders()
      .then((data) => {
        setOrders(Array.isArray(data) ? data : (data.content || []));
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Failed to load orders');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
        ))}
      </div>
    );
  }

  if (error) return <div className="alert alert-error">{error}</div>;

  if (orders.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🛒</div>
        <h3>No orders yet</h3>
      </div>
    );
  }

  return (
    <div className="card table-wrap">
      <table>
        <thead>
          <tr>
            <th>Order Ref</th>
            <th>Date</th>
            <th>Buyer</th>
            <th>Phone</th>
            <th>Address</th>
            <th>City</th>
            <th>Items</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Delivery Time</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const ref         = order.order_ref    || order.orderRef    || `#${order.id}`;
            const date        = order.created_at   || order.createdAt   || null;
            const buyer       = order.buyer_name   || order.buyerName   || '—';
            const phone       = order.buyer_phone  || order.phone       || '—';
            const address     = order.buyer_address || order.buyerAddress || '—';
            const amount      = order.total_amount  || order.totalAmount  || order.total || 0;
            const deliveredAt = order.delivered_at  || order.deliveredAt  || null;
            const deliveryMins = (date && deliveredAt)
              ? (new Date(deliveredAt) - new Date(date)) / 60000
              : null;
            const deliveryStr = fmtDuration(deliveryMins);
            return (
              <tr key={order.id}>
                <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{ref}</td>
                <td style={{ color: 'var(--muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {date
                    ? new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—'}
                </td>
                <td style={{ fontWeight: 600 }}>{buyer}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{phone}</td>
                <td style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 180 }}>{address}</td>
                <td>{order.city || '—'}</td>
                <td style={{ color: 'var(--muted)' }}>
                  {order.items ? order.items.length : '—'}
                </td>
                <td style={{ fontWeight: 700 }}>₹{Number(amount).toFixed(2)}</td>
                <td>
                  <span className={`badge ${orderStatusBadgeClass(order.status)}`}>
                    {(order.status || 'pending').toUpperCase()}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: '#0891b2', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {deliveryStr || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminGetStats()
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Failed to load stats');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="stats-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 96, borderRadius: 12 }} />
        ))}
      </div>
    );
  }

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!stats) return null;

  const cards = [
    {
      icon: '🏪',
      label: 'Total Sellers',
      value: stats.totalSellers ?? stats.sellers ?? 0,
      bg: '#eff6ff',
    },
    {
      icon: '📦',
      label: 'Active Products',
      value: stats.activeProducts ?? stats.products ?? 0,
      bg: '#f0fdf4',
    },
    {
      icon: '🛒',
      label: 'Total Orders',
      value: stats.totalOrders ?? stats.orders ?? 0,
      bg: '#fff7ed',
    },
    {
      icon: '💰',
      label: 'Total Revenue',
      value: `₹${(stats.totalRevenue ?? stats.revenue ?? 0).toLocaleString('en-IN')}`,
      bg: '#faf5ff',
    },
  ];

  return (
    <div>
      <div className="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="stat-card">
            <div className="stat-icon" style={{ background: c.bg }}>
              {c.icon}
            </div>
            <div>
              <div className="stat-value">{c.value}</div>
              <div className="stat-label">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {stats.pendingSellers !== undefined && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Quick Overview</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
            {stats.pendingSellers !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Pending Seller Approvals</span>
                <strong style={{ color: 'var(--warning)' }}>{stats.pendingSellers}</strong>
              </div>
            )}
            {stats.approvedSellers !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Approved Sellers</span>
                <strong style={{ color: 'var(--success)' }}>{stats.approvedSellers}</strong>
              </div>
            )}
            {stats.pendingOrders !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Pending Orders</span>
                <strong style={{ color: 'var(--warning)' }}>{stats.pendingOrders}</strong>
              </div>
            )}
            {stats.deliveredOrders !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Delivered Orders</span>
                <strong style={{ color: 'var(--success)' }}>{stats.deliveredOrders}</strong>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── helpers shared by AdminDashboardTab ──────────────────────────────────────
function fmtPeriod(period, view) {
  if (!period) return '';
  const s = String(period);
  if (view === 'day')   { const d = new Date(s + 'T00:00:00'); return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  if (view === 'week')  { const d = new Date(s + 'T00:00:00'); return 'Wk ' + d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  if (view === 'month') { const [y, m] = s.split('-'); return new Date(+y, +m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }); }
  return s;
}

function AdminBarChart({ data, view, metric }) {
  if (!data || data.length === 0)
    return <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '28px 0', fontSize: 13 }}>No data for this period</div>;

  const isItems  = metric === 'items';
  const getValue = d => isItems ? parseInt(d.items_sold || 0) : parseFloat(d.revenue || 0);
  const maxVal   = Math.max(...data.map(getValue), 1);
  const BAR_H    = 110;
  const barColor = isItems ? 'linear-gradient(180deg,#7c3aed,#a78bfa)' : 'linear-gradient(180deg,#2563eb,#60a5fa)';

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, minWidth: data.length * 48, padding: '8px 4px 0' }}>
        {data.map((d, i) => {
          const val    = getValue(d);
          const rev    = parseFloat(d.revenue || 0);
          const items  = parseInt(d.items_sold || 0);
          const orders = parseInt(d.orders || 0);
          const h      = Math.max(4, Math.round((val / maxVal) * BAR_H));
          const topLabel = isItems ? val : (rev >= 1000 ? '₹' + (rev / 1000).toFixed(1) + 'k' : '₹' + rev.toFixed(0));
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 40px' }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 3 }}>{topLabel}</div>
              <div
                title={`₹${rev.toFixed(2)} · ${items} items · ${orders} orders`}
                style={{ width: '100%', height: h, borderRadius: '4px 4px 0 0', background: barColor, cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              />
              <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 4, textAlign: 'center', lineHeight: 1.2 }}>
                {fmtPeriod(d.period, view)}
              </div>
              <div style={{ fontSize: 9, fontWeight: 600, color: isItems ? '#7c3aed' : '#2563eb' }}>
                {isItems ? (items > 0 ? `₹${rev.toFixed(0)}` : '') : (items > 0 ? `${items} items` : '')}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'right', marginTop: 4 }}>
        {isItems ? 'bar = items sold · below = revenue' : 'bar = revenue · below = items sold'}
      </div>
    </div>
  );
}

function AdminDashboardTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [view, setView]     = useState('month');
  const [metric, setMetric] = useState('revenue');
  const [search, setSearch] = useState('');

  useEffect(() => {
    adminGetDashboard()
      .then(setData)
      .catch(e => setError(e.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div>
      {[80, 80, 200, 260].map((h, i) => (
        <div key={i} className="skeleton" style={{ height: h, marginBottom: 12, borderRadius: 12 }} />
      ))}
    </div>
  );
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!data)  return null;

  const totals    = data.totals    || {};
  const byProduct = data.byProduct || [];
  const bySeller  = data.bySeller  || [];
  const chartData = { day: data.byDay, week: data.byWeek, month: data.byMonth, year: data.byYear }[view] || [];

  const avgDeliveryStr = fmtDuration(totals.avgDeliveryMinutes);
  const summaryCards = [
    { icon: '🛒', label: 'Total Orders',    value: totals.orders   || 0,                              color: '#2563eb', bg: '#eff6ff' },
    { icon: '📦', label: 'Items Sold',      value: totals.items    || 0,                              color: '#7c3aed', bg: '#f5f3ff' },
    { icon: '💰', label: 'Total Revenue',   value: '₹' + Number(totals.revenue  || 0).toFixed(2),    color: '#16a34a', bg: '#f0fdf4' },
    { icon: '🏪', label: 'Active Sellers',  value: totals.sellers  || 0,                              color: '#d97706', bg: '#fffbeb' },
    { icon: '🗂️', label: 'Active Products', value: totals.products || 0,                              color: '#0891b2', bg: '#ecfeff' },
    { icon: '🕐', label: 'Avg Delivery',    value: avgDeliveryStr || '—',                             color: '#7c3aed', bg: '#f5f3ff',
      sub: `${totals.deliveredCount || 0} delivered` },
  ];

  const filteredProducts = byProduct.filter(p =>
    !search ||
    String(p.product_name || '').toLowerCase().includes(search.toLowerCase()) ||
    String(p.seller_name  || '').toLowerCase().includes(search.toLowerCase()) ||
    String(p.product_id   || '').includes(search)
  );

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="card" style={{ padding: '14px 12px', background: c.bg, border: `1px solid ${c.color}22` }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.color, marginTop: 2 }}>{c.label}</div>
            {c.sub && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{c.sub}</div>}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {metric === 'items' ? 'Items Sold Over Time' : 'Revenue Over Time'}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={() => setMetric('revenue')} className={metric === 'revenue' ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'} style={{ fontSize: 12, padding: '4px 10px' }}>₹ Revenue</button>
            <button onClick={() => setMetric('items')}   className={metric === 'items'   ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'} style={{ fontSize: 12, padding: '4px 10px', background: metric === 'items' ? '#7c3aed' : undefined, borderColor: metric === 'items' ? '#7c3aed' : undefined }}># Items</button>
            <span style={{ width: 8 }} />
            {[['day','Day'],['week','Week'],['month','Month'],['year','Year']].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} className={view === v ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'} style={{ fontSize: 12, padding: '4px 10px' }}>{l}</button>
            ))}
          </div>
        </div>
        <AdminBarChart data={chartData} view={view} metric={metric} />
      </div>

      {/* Sales by Seller */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Sales by Seller
        </div>
        {bySeller.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>No data yet</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Seller</th>
                  <th>City</th>
                  <th style={{ textAlign: 'right' }}>Orders</th>
                  <th style={{ textAlign: 'right' }}>Items Sold</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                  <th style={{ textAlign: 'right' }}>Avg Delivery</th>
                </tr>
              </thead>
              <tbody>
                {bySeller.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{s.business_name || '—'}</td>
                    <td style={{ color: 'var(--muted)', fontSize: 13 }}>{s.city || '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{s.orders}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{s.items_sold}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>₹{Number(s.revenue).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: '#0891b2', fontWeight: 600 }}>
                      {fmtDuration(s.avg_delivery_minutes) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Items sold by product */}
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Items Sold by Product
          </div>
          <input
            className="form-control"
            style={{ maxWidth: 220, padding: '5px 10px', fontSize: 13 }}
            placeholder="Search product or seller…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {filteredProducts.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>No products found</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product Name</th>
                  <th>Seller</th>
                  <th>City</th>
                  <th style={{ textAlign: 'right' }}>Orders</th>
                  <th style={{ textAlign: 'right' }}>Items Sold</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)' }}>#{p.product_id}</td>
                    <td style={{ fontWeight: 600 }}>{p.product_name || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--muted)' }}>{p.seller_name || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--muted)' }}>{p.city || '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{p.orders}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{p.items_sold}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>₹{Number(p.revenue).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const FORECAST_PERIODS = [
  { key: 'req_day',       label: 'Day'      },
  { key: 'req_week',      label: 'Week'     },
  { key: 'req_month',     label: 'Month'    },
  { key: 'req_quarter',   label: 'Quarter'  },
  { key: 'req_half_year', label: '6 Months' },
  { key: 'req_year',      label: 'Year'     },
];

function stockStatus(current, required) {
  if (required === 0) return 'none';
  if (current === 0)  return 'out';
  if (current < required) return 'low';
  return 'ok';
}

function AdminStockForecastTab() {
  const [rows, setRows]         = useState([]);
  const [sellers, setSellers]   = useState([]);
  const [sellerFilter, setSellerFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [saving, setSaving]     = useState({});
  const [thresholds, setThresholds] = useState({});
  const [saved, setSaved]       = useState({});

  const load = useCallback((sid) => {
    setLoading(true);
    adminGetStockForecast(sid || undefined)
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        const init = {};
        list.forEach(r => { init[r.id] = r.alert_threshold > 0 ? String(r.alert_threshold) : ''; });
        setThresholds(init);
        // Build unique seller list
        const seen = {};
        const sl = [];
        list.forEach(r => {
          if (r.seller_id && !seen[r.seller_id]) {
            seen[r.seller_id] = true;
            sl.push({ id: r.seller_id, name: r.seller_name });
          }
        });
        setSellers(sl);
        setLoading(false);
      })
      .catch(e => { setErr(e.message || 'Failed to load forecast'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleSellerFilter(sid) {
    setSellerFilter(sid);
    load(sid || null);
  }

  async function handleSaveAlert(productId) {
    setSaving(s => ({ ...s, [productId]: true }));
    const val = parseInt(thresholds[productId], 10);
    try {
      if (!val || val <= 0) {
        await adminDeleteStockAlert(productId);
        setRows(prev => prev.map(r => r.id === productId ? { ...r, alert_threshold: 0 } : r));
      } else {
        await adminSetStockAlert(productId, val);
        setRows(prev => prev.map(r => r.id === productId ? { ...r, alert_threshold: val } : r));
      }
      setSaved(s => ({ ...s, [productId]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [productId]: false })), 1800);
    } catch (e) {
      setErr(e.message || 'Failed to save alert');
    } finally {
      setSaving(s => ({ ...s, [productId]: false }));
    }
  }

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) ||
           (r.seller_name || '').toLowerCase().includes(q);
  });

  const alertCount = filtered.filter(r => r.alert_threshold > 0 && r.stock < r.alert_threshold).length;
  const outCount   = filtered.filter(r => r.stock === 0).length;

  return (
    <div>
      {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="form-control" style={{ maxWidth: 220, fontSize: 13 }}
          value={sellerFilter} onChange={e => handleSellerFilter(e.target.value)}>
          <option value="">All Sellers</option>
          {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input className="form-control" placeholder="Search product or seller…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 240, fontSize: 13 }} />
        <button className="btn btn-outline btn-sm" onClick={() => load(sellerFilter || null)}>↻ Refresh</button>
      </div>

      {/* Summary badges */}
      {(alertCount > 0 || outCount > 0) && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {outCount > 0 && (
            <div style={{ background: '#fce8e6', border: '1px solid #f28b82', borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🚫</span>
              <div>
                <div style={{ fontWeight: 700, color: '#c5221f', fontSize: 13 }}>{outCount} product{outCount > 1 ? 's' : ''} out of stock</div>
                <div style={{ fontSize: 11, color: '#888' }}>Sellers need to restock immediately</div>
              </div>
            </div>
          )}
          {alertCount > 0 && (
            <div style={{ background: '#fef3cd', border: '1px solid #fbbc04', borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, color: '#b45309', fontSize: 13 }}>{alertCount} product{alertCount > 1 ? 's' : ''} below alert threshold</div>
                <div style={{ fontSize: 11, color: '#888' }}>Stock has fallen below admin alert limit</div>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
        ))
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <h3>No products found</h3>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            {filtered.length} product{filtered.length !== 1 ? 's' : ''} — requirements derived from average daily sales rate. Set admin alerts to monitor stock levels across sellers.
          </div>
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 160 }}>Product</th>
                  <th style={{ minWidth: 130 }}>Seller</th>
                  <th style={{ textAlign: 'right', minWidth: 80 }}>Stock</th>
                  <th style={{ textAlign: 'right', minWidth: 80 }}>Daily Rate</th>
                  {FORECAST_PERIODS.map(p => (
                    <th key={p.key} style={{ textAlign: 'right', minWidth: 70 }}>
                      {p.label}<div style={{ fontSize: 9, fontWeight: 400, color: '#aaa' }}>min qty</div>
                    </th>
                  ))}
                  <th style={{ minWidth: 170 }}>Admin Alert</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const belowAlert = row.alert_threshold > 0 && row.stock < row.alert_threshold;
                  return (
                    <tr key={row.id} style={{ background: row.stock === 0 ? '#fff5f5' : belowAlert ? '#fffbea' : undefined }}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {row.stock === 0 && <span title="Out of stock" style={{ marginRight: 4 }}>🚫</span>}
                          {belowAlert && row.stock > 0 && <span title="Below alert" style={{ marginRight: 4 }}>⚠️</span>}
                          {row.name}
                        </div>
                        <div style={{ fontSize: 11, color: '#888' }}>{row.unit}</div>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <div style={{ fontWeight: 600 }}>{row.seller_name}</div>
                        {row.seller_city && <div style={{ color: '#888', fontSize: 11 }}>{row.seller_city}</div>}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                        color: row.stock === 0 ? '#c5221f' : belowAlert ? '#b45309' : '#137333' }}>
                        {row.stock}
                      </td>
                      <td style={{ textAlign: 'right', color: '#555', fontSize: 12 }}>
                        {row.daily_rate > 0 ? row.daily_rate.toFixed(2) : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      {FORECAST_PERIODS.map(p => {
                        const req = row[p.key];
                        const st  = stockStatus(row.stock, req);
                        const color = st === 'out' ? '#c5221f' : st === 'low' ? '#b45309' : st === 'ok' ? '#137333' : '#bbb';
                        const bg    = st === 'out' ? '#fce8e6' : st === 'low' ? '#fef3cd' : 'transparent';
                        return (
                          <td key={p.key} style={{ textAlign: 'right' }}>
                            <span style={{ background: bg, color, borderRadius: 6, padding: '2px 6px', fontWeight: req > 0 ? 600 : 400, fontSize: 12 }}>
                              {req > 0 ? req : <span style={{ color: '#ccc' }}>—</span>}
                            </span>
                          </td>
                        );
                      })}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="number" min={0} placeholder="e.g. 10"
                            value={thresholds[row.id] ?? ''}
                            onChange={e => setThresholds(t => ({ ...t, [row.id]: e.target.value }))}
                            style={{ width: 72, padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 12 }} />
                          <button className="btn btn-sm btn-primary" disabled={saving[row.id]}
                            onClick={() => handleSaveAlert(row.id)}
                            style={{ padding: '4px 10px', fontSize: 12, flexShrink: 0 }}>
                            {saved[row.id] ? '✓' : saving[row.id] ? '…' : '🔔 Set'}
                          </button>
                          {row.alert_threshold > 0 && (
                            <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>@ {row.alert_threshold}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 10 }}>
            🟢 sufficient &nbsp;🟡 below requirement &nbsp;🔴 out of stock &nbsp;— no sales data yet
          </div>
        </>
      )}
    </div>
  );
}

const COMMON_ICONS = ['🛒','🥦','🍎','🥛','🐟','🍿','🥤','📱','👕','🏠','📚','📦','🌾','🍚','🧴','💊','🪴','🧁','🍕','🚗','⚽','🎮','💻','🧸','🪡','🧵','🪞','🛋️','🔧','🧰'];

function CategoryModal({ cat, onClose, onSaved }) {
  const editing = !!cat;
  const [form, setForm] = useState({ name: cat?.name || '', icon: cat?.icon || '📦', sortOrder: cat?.sort_order || 0 });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (!form.name.trim()) { setErr('Category name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await adminUpdateCategory(cat.id, { name: form.name.trim(), icon: form.icon, sortOrder: Number(form.sortOrder) });
      } else {
        await adminCreateCategory({ name: form.name.trim(), icon: form.icon, sortOrder: Number(form.sortOrder) });
      }
      onSaved();
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{editing ? '✏️ Edit Category' : '➕ New Category'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Category Name *</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Fruits, Electronics…" required />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Icon (emoji)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 32 }}>{form.icon}</span>
                <input className="form-input" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="Paste emoji" style={{ width: 90 }} maxLength={8} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {COMMON_ICONS.map(ic => (
                  <button type="button" key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
                    style={{ fontSize: 20, background: form.icon === ic ? '#e8f0fe' : '#f8f9fa', border: form.icon === ic ? '2px solid #1a73e8' : '1px solid #e8eaed', borderRadius: 8, padding: '4px 6px', cursor: 'pointer' }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label className="form-label">Sort Order</label>
              <input className="form-input" type="number" min={0} value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))} style={{ width: 100 }} />
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Lower number = appears first. Default 0.</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn btn-ghost btn-full" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-full" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Category'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | {cat object}
  const [deleting, setDeleting] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    adminGetCategories()
      .then(data => { setCats(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(e => { setErr(e.message || 'Failed to load categories'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(cat) {
    if (!window.confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    setDeleting(d => ({ ...d, [cat.id]: true }));
    try {
      await adminDeleteCategory(cat.id);
      setCats(prev => prev.filter(c => c.id !== cat.id));
    } catch (e) {
      setErr(e.message || 'Delete failed');
    } finally {
      setDeleting(d => ({ ...d, [cat.id]: false }));
    }
  }

  return (
    <div>
      {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#888' }}>{cats.length} categories</div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>➕ New Category</button>
      </div>

      {loading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8, borderRadius: 8 }} />
        ))
      ) : cats.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏷️</div>
          <h3>No categories yet</h3>
          <p>Add a category so sellers can group their products.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>Icon</th>
                <th>Name</th>
                <th style={{ textAlign: 'right', width: 100 }}>Products</th>
                <th style={{ textAlign: 'right', width: 80 }}>Order</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map(cat => (
                <tr key={cat.id}>
                  <td style={{ fontSize: 22, textAlign: 'center' }}>{cat.icon}</td>
                  <td style={{ fontWeight: 600 }}>{cat.name}</td>
                  <td style={{ textAlign: 'right', color: cat.product_count > 0 ? '#1a73e8' : '#aaa' }}>
                    {cat.product_count}
                  </td>
                  <td style={{ textAlign: 'right', color: '#888' }}>{cat.sort_order}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setModal(cat)}>✏️ Edit</button>
                      <button className="btn btn-sm" style={{ background: '#fce8e6', color: '#c5221f', border: 'none' }}
                        onClick={() => handleDelete(cat)} disabled={deleting[cat.id]}>
                        {deleting[cat.id] ? '…' : '🗑️'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <CategoryModal
          cat={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('dashboard');

  function handleSignOut() {
    window.location.href = '/spring/logout';
  }

  return (
    <div className="page-container">
      <div className="mk-admin-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 800, fontSize: 22 }}>Admin Panel</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
            Manage sellers, orders, and marketplace data
          </p>
        </div>
        <button
          onClick={handleSignOut}
          className="btn btn-outline"
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
        >
          🚪 Sign Out
        </button>
      </div>

      <div className="tabs">
        <button className={`tab${activeTab === 'dashboard'  ? ' active' : ''}`} onClick={() => setActiveTab('dashboard')}>📊 Dashboard</button>
        <button className={`tab${activeTab === 'sellers'    ? ' active' : ''}`} onClick={() => setActiveTab('sellers')}>🏪 Sellers</button>
        <button className={`tab${activeTab === 'orders'     ? ' active' : ''}`} onClick={() => setActiveTab('orders')}>🛒 Orders</button>
        <button className={`tab${activeTab === 'stock'      ? ' active' : ''}`} onClick={() => setActiveTab('stock')}>📈 Stock Forecast</button>
        <button className={`tab${activeTab === 'categories' ? ' active' : ''}`} onClick={() => setActiveTab('categories')}>🏷️ Categories</button>
        <button className={`tab${activeTab === 'stats'      ? ' active' : ''}`} onClick={() => setActiveTab('stats')}>📈 Stats</button>
      </div>

      {activeTab === 'dashboard'  && <AdminDashboardTab />}
      {activeTab === 'sellers'    && <SellersTab />}
      {activeTab === 'orders'     && <OrdersTab />}
      {activeTab === 'stock'      && <AdminStockForecastTab />}
      {activeTab === 'categories' && <CategoriesTab />}
      {activeTab === 'stats'      && <StatsTab />}
    </div>
  );
}
